import * as React from "react";
import { cn } from "../../lib/utils";
import { Badge } from "./badge";

/**
 * The entrance to a page.
 *
 * Every screen opened with its own hand-rolled `<h1>` + description before
 * this, which meant twelve slightly different spacings and no shared place to
 * change them. One component instead.
 *
 * The treatment is a short flame rule, context above the title (an eyebrow on
 * top-level pages, a breadcrumb on nested ones), then the title, an optional
 * status badge and the description — with actions right-aligned. The rule is
 * the only decoration: it marks the start of the page without adding a box or
 * a border, which the surfaces rule (DESIGN_SYSTEM.md §6) wouldn't allow.
 *
 * Detail pages (a project, a service, a deployment) pass `icon` for the tile
 * and `badge` for status, and may pass a ReactNode `title` when the name is
 * editable in place.
 *
 * `text-h1` is the page title and there is **one per page** — nested headings
 * use `text-h2`/`text-h3`. Pass `as="h2"` only when the page already has an
 * h1 above it, which keeps the document outline honest without changing the
 * way it looks.
 */
export interface PageHeaderProps {
  /** The page title. One per page. A node when it's editable in place. */
  title: React.ReactNode;
  /** Short uppercase context above the title, e.g. "WORKSPACE". */
  eyebrow?: string;
  /** Hierarchy context for a nested page. Replaces `eyebrow` when both exist. */
  breadcrumb?: React.ReactNode;
  /** Icon for the tile left of the title, on detail pages. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Status badge or similar, shown inline after the title. */
  badge?: React.ReactNode;
  /** One line under the title saying what the page is for. */
  description?: React.ReactNode;
  /** Richer metadata under the title — IDs, copy buttons, timestamps. */
  meta?: React.ReactNode;
  /** A live count shown next to the title, e.g. the number of projects. */
  count?: number;
  /** Buttons or controls, right-aligned and bottom-aligned with the title. */
  actions?: React.ReactNode;
  /** Heading level. Defaults to h1; use h2 when an h1 already exists above. */
  as?: "h1" | "h2";
  className?: string;
}

export function PageHeader({
  title,
  eyebrow,
  breadcrumb,
  icon: Icon,
  badge,
  description,
  meta,
  count,
  actions,
  as: Heading = "h1",
  className,
}: PageHeaderProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {/* Breadcrumb sits above the rule: it's navigation to get here, not part
          of the page's own heading. */}
      {breadcrumb}

      {/* The accent rule. Decorative, so it's hidden from assistive tech.
          It draws itself in from the left — the one flourish on the page, and
          it reads as the page "opening" rather than as decoration moving. */}
      <div
        className="h-0.5 w-10 origin-left animate-draw-in rounded-full bg-primary"
        aria-hidden="true"
      />

      <div className="flex animate-fade-in-up flex-wrap items-end justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 items-start gap-3">
          {Icon && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Icon className="h-5 w-5 text-muted-foreground" />
            </div>
          )}

          <div className="min-w-0 space-y-1">
            {/* A breadcrumb already says where you are, so the eyebrow would
                only repeat it. */}
            {eyebrow && !breadcrumb && (
              <p className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
                {eyebrow}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Heading className="truncate text-h1">{title}</Heading>
              {/* `count != null` so a genuine 0 still renders — "0 projects" is
                  information, and hiding it makes an empty page look broken. */}
              {count != null && (
                <Badge variant="secondary" className="shrink-0">
                  {count}
                </Badge>
              )}
              {badge}
            </div>

            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
            {meta}
          </div>
        </div>

        {actions && (
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
